import cx from "classnames";
import React from "react";
import { type UseFormRegisterReturn } from "react-hook-form";
import { Icon } from "@iconify/react";

type InputProps = Partial<UseFormRegisterReturn> & {
  type?: string;
  label?: string;
  placeholder?: string;
  className?: string;
  autoComplete?: string;
};

type InputCheckboxProps = Partial<UseFormRegisterReturn> & {
  label?: string;
  name?: string;
  className?: string;
  inputClassName?: string;
  checked?: boolean;
  defaultChecked?: boolean;
};

const wrapperClass = "flex flex-col gap-1.5 w-full";
const baseInputClass =
  "peer block w-full h-14 rounded-[10px] border border-slate-300 bg-white px-4 pb-2 pt-5 text-[18px] text-slate-900 placeholder:text-transparent shadow-[0_1px_2px_rgba(15,23,42,0.06)] outline-none transition-all duration-150 focus:border-brand-green focus:ring-2 focus:ring-brand-green disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-500";
const floatingLabelClass =
  "pointer-events-none absolute left-4 z-10 bg-transparent px-1 font-medium transition-all duration-150 rounded-sm";

function useFloatingLabelState(inputRef: React.RefObject<HTMLInputElement | null>) {
  const [isFocused, setIsFocused] = React.useState(false);
  const [hasValue, setHasValue] = React.useState(false);

  const syncHasValue = React.useCallback(() => {
    if (inputRef.current) {
      setHasValue(
        Boolean(inputRef.current?.value) ||
          window.getComputedStyle(inputRef.current, null).getPropertyValue("appearance") === "menulist-button",
      );
    }
  }, [inputRef]);

  React.useEffect(() => {
    const timeouts = [0, 100, 300, 700, 2000].map((delay) => window.setTimeout(syncHasValue, delay));
    return () => {
      timeouts.forEach((id) => window.clearTimeout(id));
    };
  }, [syncHasValue]);

  return {
    isFocused,
    hasValue,
    setIsFocused,
    syncHasValue,
    isFloating: isFocused || hasValue,
  };
}

const Input = (props: InputProps) => {
  const generatedId = React.useId();
  const inputId = props.name || generatedId;
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const { isFocused, isFloating, setIsFocused, syncHasValue } = useFloatingLabelState(inputRef);

  const setRefs = React.useCallback(
    (node: HTMLInputElement | null) => {
      inputRef.current = node;
      if (typeof props.ref === "function") {
        props.ref(node);
      }
      syncHasValue();
    },
    [props, syncHasValue],
  );

  return (
    <>
      <div className={wrapperClass}>
        <div className="relative">
          <input
            {...props}
            id={inputId}
            ref={setRefs}
            placeholder={props.placeholder || " "}
            className={cx(baseInputClass, props.className)}
            onFocus={() => {
              setIsFocused(true);
            }}
            onBlur={(event) => {
              setIsFocused(false);
              syncHasValue();
              props.onBlur?.(event);
            }}
            onChange={(event) => {
              syncHasValue();
              props.onChange?.(event);
            }}
            onInput={syncHasValue}
          />
          {props.label && (
            <label
              className={cx(
                floatingLabelClass,
                isFloating
                  ? "top-3 -translate-y-1/2 text-xs text-slate-600"
                  : "top-1/2 -translate-y-1/2 text-[18px] text-slate-500",
                isFocused && "text-emerald-900",
              )}
              htmlFor={inputId}
            >
              {props.label}
            </label>
          )}
        </div>
      </div>
    </>
  );
};

export function InputPassword(props: InputProps) {
  const [passwordShown, setPasswordShown] = React.useState(false);
  const generatedId = React.useId();
  const inputId = props.name || generatedId;
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const { isFocused, isFloating, setIsFocused, syncHasValue } = useFloatingLabelState(inputRef);

  const setRefs = React.useCallback(
    (node: HTMLInputElement | null) => {
      inputRef.current = node;
      if (typeof props.ref === "function") {
        props.ref(node);
      }
      syncHasValue();
    },
    [props, syncHasValue],
  );

  const togglePasswordVisiblity = () => {
    setPasswordShown((shown) => !shown);
  };
  return (
    <div className={wrapperClass}>
      <div className="relative">
        <input
          {...props}
          id={inputId}
          ref={setRefs}
          type={passwordShown ? "text" : "password"}
          placeholder={props.placeholder || " "}
          className={cx(baseInputClass, "pr-11", props.className)}
          onFocus={() => {
            setIsFocused(true);
          }}
          onBlur={(event) => {
            setIsFocused(false);
            syncHasValue();
            props.onBlur?.(event);
          }}
          onChange={(event) => {
            syncHasValue();
            props.onChange?.(event);
          }}
          onInput={syncHasValue}
        />
        {props.label && (
          <label
            className={cx(
              floatingLabelClass,
              isFloating
                ? "top-3 -translate-y-1/2 text-xs text-slate-600"
                : "top-1/2 -translate-y-1/2 text-[18px] text-slate-500",
              isFocused && "text-emerald-900",
            )}
            htmlFor={inputId}
          >
            {props.label}
          </label>
        )}
        <div
          onClick={togglePasswordVisiblity}
          className="absolute inset-y-0 right-0 flex items-center pr-3 text-xl cursor-pointer text-slate-500 hover:text-slate-700"
        >
          {passwordShown ? <Icon icon={"prime:eye"} /> : <Icon icon={"prime:eye-slash"} />}
        </div>
      </div>
    </div>
  );
}

export function InputCheckbox({
  label,
  className,
  inputClassName,
  checked,
  defaultChecked,
  ...props
}: InputCheckboxProps) {
  return (
    <label
      className={cx(
        "flex items-start gap-3 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700",
        className,
      )}
    >
      <input
        type="checkbox"
        {...props}
        checked={checked}
        defaultChecked={defaultChecked}
        className={cx("mt-0.5 h-4 w-4 rounded border-slate-300 accent-emerald-600", inputClassName)}
      />
      <span>{label || ""}</span>
    </label>
  );
}

export default Input;
